import {
  Outlet,
  createRoute,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { AppHeader } from '../components/app-header'
import { MeProvider, type Me } from '../lib/me-context'
import { Route as rootRoute } from './__root'

/**
 * Authenticated app layout — the parent of every host-facing route that
 * requires a signed-in session.
 *
 * Resolves the current identity with a single `/api/v1/auth/me` round-trip
 * (cached by TanStack Query under the `auth/me` key so every child route
 * reuses the same fetch). The three terminal states drive the shell:
 *
 *   - **loading** → a minimal centred spinner; we keep the chrome empty so
 *     the spinner is not framed by stale layout from a previous user.
 *   - **error (401 / network)** → an effect schedules a `navigate({ to:
 *     '/auth/sign-in' })`; we still render `null` underneath so we do not
 *     paint a half-authenticated shell during the redirect frame.
 *   - **success** → `<Outlet />` renders the child route.
 *
 * Using `id: '_app'` makes this a pathless layout route under TanStack
 * Router's file-based conventions — children declared under `_app/…`
 * inherit this auth gate without needing to repeat the check themselves.
 */
export const Route = createRoute({
  getParentRoute: () => rootRoute,
  id: '_app',
  component: AppLayout,
})

function AppLayout() {
  const navigate = useNavigate()
  const { t: tCommon } = useTranslation('common')
  // The live game surfaces (`/games/host/*`, `/games/play/*`) are immersive,
  // full-bleed projector views; the app header would intrude on them, so we
  // suppress the shared chrome there while keeping it on every authoring page.
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const showHeader = !pathname.startsWith('/games/')

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async ({ signal }) => {
      const res = await fetch('/api/v1/auth/me', {
        credentials: 'include',
        signal,
      })
      if (res.status === 401) {
        // Distinguish unauthorized from a transport-level failure so the
        // redirect effect can act on it without conflating network errors.
        throw new UnauthorizedError()
      }
      if (!res.ok) {
        throw new Error(`Failed to load session: ${res.status}`)
      }
      return (await res.json()) as Me
    },
    retry: (_failureCount, error) => !(error instanceof UnauthorizedError),
    // Re-validate the session whenever the tab regains focus. A user who is
    // AFK for an hour and comes back has very likely had their auth cookie
    // expire server-side; refetching on focus (rather than waiting for a
    // manual refresh or the next navigation) lets the 401 below fire and
    // bounce them to sign-in immediately. `staleTime: 0` ensures the focus
    // refetch actually runs instead of being served stale from cache.
    refetchOnWindowFocus: true,
    staleTime: 0,
  })

  useEffect(() => {
    if (meQuery.error instanceof UnauthorizedError) {
      // Preserve where the user was so sign-in can send them back after they
      // re-authenticate. Strips the origin; keeps path + query + hash.
      //
      // Guard against the navigation race: when we navigate to /auth/sign-in,
      // this layout briefly re-renders with the NEW pathname while the error
      // is still set, which would re-fire this effect and clobber the good
      // returnTo with "/auth/sign-in". Never capture an auth path as the
      // return target — there's nothing useful to return to there.
      if (pathname.startsWith('/auth/')) return
      const returnTo = `${pathname}${window.location.search}${window.location.hash}`
      void navigate({
        to: '/auth/sign-in',
        search: { redirect: returnTo },
      })
    }
  }, [meQuery.error, navigate, pathname])

  if (meQuery.isLoading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-ink-50">
        <div
          className="h-12 w-12 animate-spin rounded-full border-4 border-brand-100 border-t-brand-500"
          role="status"
          aria-live="polite"
        >
          <span className="sr-only">{tCommon('loading')}</span>
        </div>
      </main>
    )
  }

  if (meQuery.error instanceof UnauthorizedError) {
    // Redirect is being scheduled in the effect above; render nothing in
    // the interim so we never flash app content to a signed-out viewer.
    return null
  }

  if (meQuery.isError) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-ink-50 px-4">
        <div
          className="max-w-md rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 text-center"
          role="alert"
        >
          {tCommon('error')}
        </div>
      </main>
    )
  }

  if (!meQuery.data) {
    // Defensive: the loading/error branches above already cover the
    // not-yet-resolved states, but this narrows `data` from `Me | undefined`
    // so MeProvider always receives a concrete identity.
    return null
  }

  return (
    <MeProvider me={meQuery.data}>
      {showHeader ? <AppHeader /> : null}
      <Outlet />
    </MeProvider>
  )
}

/**
 * Sentinel error so the `useQuery.retry` policy can short-circuit on 401
 * (we never want to hammer `/auth/me` after an explicit unauthorized).
 */
class UnauthorizedError extends Error {
  constructor() {
    super('Unauthorized')
    this.name = 'UnauthorizedError'
  }
}
